import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

export const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className='min-h-screen bg-base text-main'>
      <div className='max-w-[680px] mx-auto px-5 py-8'>
        <button
          type='button'
          onClick={() => navigate(-1)}
          className='flex items-center gap-1 text-muted mb-6 text-[13px]'
        >
          <ChevronLeft className='w-4 h-4' /> Back
        </button>

        <h1 className='text-2xl font-extrabold text-main mb-1'>Privacy Policy</h1>
        <p className='text-xs text-dim mb-8'>Effective date: 13 April 2026</p>

        <div className='space-y-6 text-sm text-muted leading-relaxed'>
          <section>
            <h2 className='text-base font-bold text-main mb-2'>1. Who We Are</h2>
            <p>
              Planacle Ltd operates the Planacle group-planning service. Contact:{' '}
              <a
                href='mailto:privacy@planacle.app'
                className='text-link underline underline-offset-2'
              >
                privacy@planacle.app
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className='text-base font-bold text-main mb-2'>2. Data We Collect</h2>
            <ul className='list-disc list-inside space-y-1 text-muted'>
              <li>Account data: name, email address, profile photo.</li>
              <li>Location data (optional): country, city, address, postcode.</li>
              <li>Availability and event preferences you submit.</li>
              <li>Usage data: pages visited, features used (anonymised).</li>
            </ul>
          </section>

          <section>
            <h2 className='text-base font-bold text-main mb-2'>3. Legal Basis (GDPR)</h2>
            <p>
              We process your data on the basis of contract performance (to provide the service) and
              your consent (marketing, optional features). You may withdraw consent at any time.
            </p>
          </section>

          <section>
            <h2 className='text-base font-bold text-main mb-2'>4. How We Use Your Data</h2>
            <ul className='list-disc list-inside space-y-1 text-muted'>
              <li>To operate and improve the Planacle service.</li>
              <li>To generate AI-powered itinerary suggestions.</li>
              <li>To send service notifications (not marketing without consent).</li>
            </ul>
          </section>

          <section>
            <h2 className='text-base font-bold text-main mb-2'>5. Third-Party Processors</h2>
            <div className='overflow-x-auto'>
              <table className='w-full text-xs border-collapse'>
                <thead>
                  <tr className='text-dim border-b border-subtle'>
                    <th className='text-left py-2 pr-4'>Service</th>
                    <th className='text-left py-2'>Purpose</th>
                  </tr>
                </thead>
                <tbody className='text-muted'>
                  {[
                    ['Google Firebase', 'Auth, database, hosting'],
                    ['Google Gemini', 'AI plan generation'],
                    ['Google Places API', 'Venue data'],
                    ['Ticketmaster API', 'Event discovery'],
                    ['Sentry', 'Error monitoring and crash reporting'],
                    ['Mixpanel', 'Product analytics and usage tracking'],
                  ].map(([service, purpose]) => (
                    <tr key={service} className='border-b border-subtle'>
                      <td className='py-2 pr-4'>{service}</td>
                      <td className='py-2'>{purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className='text-base font-bold text-main mb-2'>6. Data Retention</h2>
            <p>We retain your data for specific timeframes depending on the type:</p>
            <ul className='list-disc pl-5 mt-2 space-y-1 text-[13px]'>
              <li>
                <strong>Account data</strong> — retained while your account is active, plus 30 days
                after deletion.
              </li>
              <li>
                <strong>Event data</strong> — retained for 2 years after the event date.
              </li>
              <li>
                <strong>Analytics data</strong> — retained for 26 months.
              </li>
              <li>
                <strong>Discovery feed cache</strong> — retained for 48 hours.
              </li>
            </ul>
            <p className='mt-2'>
              You may request deletion at any time via Account Settings → &quot;Delete my
              account&quot;.
            </p>
          </section>

          <section>
            <h2 className='text-base font-bold text-main mb-2'>7. Your Rights (GDPR)</h2>
            <p className='mb-2'>You have the right to:</p>
            <ul className='list-disc list-inside space-y-1 text-muted'>
              <li>Access your data (Account Settings → &quot;Download my data&quot;).</li>
              <li>Rectify inaccurate data (edit your profile).</li>
              <li>Erase your data (Account Settings → &quot;Delete my account&quot;).</li>
              <li>Lodge a complaint with the ICO at ico.org.uk.</li>
            </ul>
          </section>

          <section>
            <h2 className='text-base font-bold text-main mb-2'>8. Contact</h2>
            <p>
              Data protection enquiries:{' '}
              <a
                href='mailto:privacy@planacle.app'
                className='text-link underline underline-offset-2'
              >
                privacy@planacle.app
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
